import type React from 'react'
import { useCallback, useState } from 'react'
import { Document, Page, pdfjs, Thumbnail } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Check, ChevronLeft, ChevronRight, FileText, LayoutGrid } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

export type PdfViewMode = 'single' | 'grid'

interface PdfPageSelectorProps {
  /** Authenticated PDF source object for react-pdf */
  pdfSource: { url: string; httpHeaders?: Record<string, string> }
  /** Total number of pages (from analysis_result) */
  pageCount: number
  /** Set of currently selected page numbers (1-indexed) */
  selectedPages: Set<number>
  /** Called when a page is toggled on/off */
  onTogglePage: (page: number) => void
  /** Controlled view mode */
  viewMode: PdfViewMode
  onViewModeChange: (mode: PdfViewMode) => void
  /** Controlled current page for single view */
  currentPage: number
  onCurrentPageChange: (page: number) => void
  /**
   * Optional content rendered inside the scrollable area below the PDF page
   * (single-view only). Use this to inject a chunk list.
   */
  children?: React.ReactNode
}

export function PdfPageSelector({
  pdfSource,
  pageCount,
  selectedPages,
  onTogglePage,
  viewMode,
  onViewModeChange,
  currentPage,
  onCurrentPageChange,
  children,
}: PdfPageSelectorProps) {
  const [loadedPageCount, setLoadedPageCount] = useState<number | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const totalPages = loadedPageCount ?? pageCount

  const handleLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setLoadedPageCount(numPages)
    setLoadError(null)
  }, [])

  const handleLoadError = useCallback((error: Error) => {
    console.error('PDF load error:', error)
    setLoadError('Failed to load PDF preview.')
  }, [])

  const goToPrev = () => onCurrentPageChange(Math.max(1, currentPage - 1))
  const goToNext = () => onCurrentPageChange(Math.min(totalPages, currentPage + 1))

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        {loadError}
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border flex-shrink-0">
        <span className="text-xs text-muted-foreground">
          {selectedPages.size > 0
            ? `${selectedPages.size} page${selectedPages.size !== 1 ? 's' : ''} selected`
            : 'Select pages to include in scope'}
        </span>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant={viewMode === 'single' ? 'secondary' : 'ghost'}
            className="h-7 px-2 text-xs"
            onClick={() => onViewModeChange('single')}
            title="Single page view"
          >
            <FileText className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            className="h-7 px-2 text-xs"
            onClick={() => onViewModeChange('grid')}
            title="Grid view"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* PDF Document wrapper */}
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <Document
          file={pdfSource}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={handleLoadError}
          className="flex flex-col h-full"
          loading={
            <div className="p-4 space-y-3">
              <Skeleton className="h-[500px] w-full" />
            </div>
          }
        >
          {viewMode === 'single' ? (
            <SinglePageView
              currentPage={currentPage}
              totalPages={totalPages}
              selected={selectedPages.has(currentPage)}
              onToggle={() => onTogglePage(currentPage)}
              onPrev={goToPrev}
              onNext={goToNext}
            >
              {children}
            </SinglePageView>
          ) : (
            <GridPageView
              totalPages={totalPages}
              selectedPages={selectedPages}
              onTogglePage={onTogglePage}
              onJumpToPage={page => {
                onCurrentPageChange(page)
                onViewModeChange('single')
              }}
            />
          )}
        </Document>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Single page view
// ---------------------------------------------------------------------------

interface SinglePageViewProps {
  currentPage: number
  totalPages: number
  selected: boolean
  onToggle: () => void
  onPrev: () => void
  onNext: () => void
  children?: React.ReactNode
}

function SinglePageView({
  currentPage,
  totalPages,
  selected,
  onToggle,
  onPrev,
  onNext,
  children,
}: SinglePageViewProps) {
  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Navigation bar — top */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border flex-shrink-0 bg-muted/50">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPrev}
          disabled={currentPage <= 1}
          className="h-7 px-2 text-xs"
        >
          <ChevronLeft className="h-3.5 w-3.5 mr-1" />
          Prev
        </Button>

        <span className="text-xs text-muted-foreground">
          Page {currentPage} of {totalPages}
        </span>

        <Button
          variant="ghost"
          size="sm"
          onClick={onNext}
          disabled={currentPage >= totalPages}
          className="h-7 px-2 text-xs"
        >
          Next
          <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>

      {/* Scrollable area: PDF page + optional children (e.g. chunk list) */}
      <div className="flex-1 overflow-y-auto flex flex-col items-center p-3 gap-4 md:p-4">
        {/* The page renders at a fixed 600px; the phone scales its canvas to the pane. */}
        <div className="relative inline-block max-md:block max-md:w-full max-md:[&_canvas]:!h-auto max-md:[&_canvas]:!w-full">
          <Page
            pageNumber={currentPage}
            width={600}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={
              <Skeleton className="h-[566px] w-[600px] max-md:h-auto max-md:w-full max-md:aspect-[600/566]" />
            }
          />
          {/* Selection toggle — top-right corner */}
          <button
            onClick={onToggle}
            className={`
              absolute top-3 right-3 z-10
              size-9 md:size-7 rounded-full border-2 flex items-center justify-center
              transition-all shadow-md
              ${
                selected
                  ? 'bg-blue-500 border-blue-500 text-white'
                  : 'bg-white/90 border-neutral-400 text-transparent hover:border-blue-400'
              }
            `}
            title={selected ? 'Remove page from scope' : 'Add page to scope'}
          >
            <Check className="h-4 w-4" />
          </button>
        </div>

        {/* Children rendered below the PDF page in the same scrollable container */}
        {children && <div className="w-full max-w-[600px]">{children}</div>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Grid view
// ---------------------------------------------------------------------------

interface GridPageViewProps {
  totalPages: number
  selectedPages: Set<number>
  onTogglePage: (page: number) => void
  onJumpToPage: (page: number) => void
}

function GridPageView({
  totalPages,
  selectedPages,
  onTogglePage,
  onJumpToPage,
}: GridPageViewProps) {
  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
          <PageThumbnailCard
            key={pageNum}
            pageNumber={pageNum}
            selected={selectedPages.has(pageNum)}
            onToggle={() => onTogglePage(pageNum)}
            onDoubleClick={() => onJumpToPage(pageNum)}
          />
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Individual thumbnail card (used in grid view)
// ---------------------------------------------------------------------------

interface PageThumbnailCardProps {
  pageNumber: number
  selected: boolean
  onToggle: () => void
  onDoubleClick: () => void
}

function PageThumbnailCard({
  pageNumber,
  selected,
  onToggle,
  onDoubleClick,
}: PageThumbnailCardProps) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      {/* Thumbnail with selection border */}
      <div
        className={`
          relative cursor-pointer rounded overflow-hidden border-2 transition-all
          max-md:w-full max-md:[&_canvas]:!h-auto max-md:[&_canvas]:!w-full
          ${selected ? 'border-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.3)]' : 'border-border hover:border-muted-foreground'}
        `}
        onClick={onToggle}
        onDoubleClick={onDoubleClick}
        title={`Page ${pageNumber} — double-click to view full page`}
      >
        <Thumbnail
          pageNumber={pageNumber}
          width={160}
          loading={<Skeleton className="h-[207px] w-[160px]" />}
        />
        {/* Checkmark overlay when selected */}
        {selected && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center shadow">
            <Check className="h-3 w-3 text-white" />
          </div>
        )}
      </div>

      {/* Page label + radio-style toggle beneath */}
      <label className="flex items-center gap-1.5 cursor-pointer select-none" onClick={onToggle}>
        {/* Visual radio/checkbox */}
        <span
          className={`
            inline-flex items-center justify-center w-4 h-4 rounded-full border-2 flex-shrink-0 transition-colors
            ${selected ? 'border-blue-500 bg-blue-500' : 'border-muted-foreground bg-transparent'}
          `}
        >
          {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
        </span>
        <span className="text-[11px] text-muted-foreground">p.{pageNumber}</span>
      </label>
    </div>
  )
}
